import { useQuery } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type LegalEntity } from '../api.ts';
import { can, canInGroup, useMe } from '../auth.tsx';
import { SearchBox, StatusFilter } from '../components/ListFilters.tsx';
import { MasterDataNote, useMasterDataReadOnly } from '../components/master-data.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Loading,
  PageHeader,
  Select,
  Table,
} from '../components/ui.tsx';
import { MasterDeleteButton } from '../components/MasterDelete.tsx';
import { EntityRecord } from './legal-entities/EntityRecord.tsx';
import {
  filterEntities,
  recordSearch,
  readRecordSearch,
  type EntityFilter,
  type EntityTab,
} from './legal-entities/record.ts';

const KINDS = ['holding', 'operating', 'other'] as const;

type Open = { id: string | null; tab?: unknown } | null;

/**
 * Empresas e Estabelecimentos: a lista das empresas do grupo, com busca e filtros, e a ficha de
 * cada uma numa janela com abas (Dados cadastrais, Filiais, Locais, Centros de custo, Integração e
 * consolidação, Procuradores e sócios). O endereço guarda a ficha aberta (?entity=…&tab=…).
 */
export function LegalEntitiesPage() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  // Em FP&A › Configurações a tela só consulta: o cadastro é mantido em Dados Mestres.
  const readOnly = useMasterDataReadOnly();
  const mayManage = canInGroup(me, 'core.legal_entity.manage');
  // Em Dados Mestres, a tela leva às Subsidiárias (no FP&A ela é aberta só para consulta).
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const inMasterData = pathname === '/legal-entities';
  const canManage = mayManage && !readOnly;
  const [filter, setFilter] = useState<EntityFilter>({ q: '', status: 'active', kind: '' });
  const [open, setOpenState] = useState<Open>(() => readRecordSearch(currentSearch()));

  const list = useQuery({
    queryKey: ['legal-entities'],
    queryFn: () => api.get<LegalEntity[]>('/core/legal-entities'),
  });

  // A ficha aberta fica no endereço, para voltar a ela ou mandar o link.
  const setOpen = (next: Open, tab?: EntityTab) => {
    setOpenState(next);
    replaceSearch(next?.id ? recordSearch(next.id, tab) : '');
  };

  const byId = new Map(list.data?.map((e) => [e.id, e]));
  const rows = list.data ? filterEntities(list.data, filter) : [];

  return (
    <>
      <PageHeader
        title={t(inMasterData ? 'legalEntities.masterDataTitle' : 'legalEntities.title')}
        description={t('legalEntities.description')}
        actions={
          <div className="flex flex-wrap gap-2">
            {inMasterData && can(me, 'structure.catalog.read') && (
              <Button
                variant="secondary"
                onClick={() => void navigate({ to: '/structure/subsidiaries' })}
              >
                {t('legalEntities.integration')}
              </Button>
            )}
            {canManage && (
              <Button onClick={() => setOpen({ id: null })}>{t('legalEntities.new')}</Button>
            )}
          </div>
        }
      />
      <MasterDataNote allowed={mayManage} />

      <div className="flex flex-wrap items-center gap-2">
        <SearchBox
          value={filter.q}
          onChange={(q) => setFilter({ ...filter, q })}
          placeholder={t('legalEntities.record.searchEntity')}
        />
        <StatusFilter
          value={filter.status}
          onChange={(status) => setFilter({ ...filter, status })}
        />
        <Select
          className="w-full sm:w-48"
          aria-label={t('legalEntities.kind')}
          value={filter.kind}
          onChange={(e) => setFilter({ ...filter, kind: e.target.value })}
        >
          <option value="">{t('legalEntities.record.allKinds')}</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`legalEntities.kinds.${k}`)}
            </option>
          ))}
        </Select>
      </div>

      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorBox error={list.error} />
      ) : (
        <Table
          head={[
            t('fields.code'),
            t('legalEntities.legalName'),
            t('fields.country'),
            t('legalEntities.taxId'),
            t('legalEntities.functionalCurrency'),
            t('legalEntities.parent'),
            t('fields.status'),
            '',
          ]}
        >
          {rows.length === 0 && (
            <EmptyRow colSpan={8}>
              {list.data.length === 0
                ? t('legalEntities.empty')
                : t('legalEntities.record.noMatch')}
            </EmptyRow>
          )}
          {rows.map((e) => (
            <tr key={e.id}>
              <td className="font-mono">{e.code}</td>
              <td>
                <button
                  type="button"
                  className="text-left font-medium hover:text-brand-text hover:underline"
                  onClick={() => setOpen({ id: e.id })}
                >
                  {e.legalName}
                </button>
                {e.tradeName && <div className="text-xs text-muted">{e.tradeName}</div>}
                <div className="text-xs text-muted">{t(`legalEntities.kinds.${e.kind}`)}</div>
              </td>
              <td>{e.country}</td>
              <td className="font-mono text-xs">{e.taxId ?? '—'}</td>
              <td className="font-mono">{e.functionalCurrency}</td>
              <td className="text-xs">
                {e.parentLegalEntityId
                  ? `${byId.get(e.parentLegalEntityId)?.code ?? '?'} · ${Number(e.ownershipPct)}%`
                  : '—'}
              </td>
              <td>
                {e.isActive ? (
                  <Badge tone="ok">{t('fields.active')}</Badge>
                ) : (
                  <Badge tone="off">{t('fields.inactive')}</Badge>
                )}
              </td>
              <td className="text-right">
                <span className="flex justify-end gap-2">
                  <Button variant="secondary" onClick={() => setOpen({ id: e.id })}>
                    {canManage ? t('common.edit') : t('legalEntities.record.open')}
                  </Button>
                  <MasterDeleteButton table="core.legal_entity" id={e.id} compact />
                </span>
              </td>
            </tr>
          ))}
        </Table>
      )}

      {open && list.data && (
        <EntityRecord
          key={open.id ?? 'new'}
          entityId={open.id}
          initialTab={open.tab}
          readOnly={readOnly}
          onClose={() => setOpen(null)}
          onCreated={(id) => setOpen({ id })}
          onTabChange={(tab) => open.id && replaceSearch(recordSearch(open.id, tab))}
        />
      )}
    </>
  );
}

function currentSearch(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

/** Troca só a parte "?…" do endereço, sem criar uma entrada nova no histórico do navegador. */
function replaceSearch(search: string) {
  if (typeof window === 'undefined') return;
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${search}`);
}
